import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { bulkAssignAssets, type BulkAssignBody, type BulkAssignResult } from '@/api/assets';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { LocationSelect, PersonPicker, pickerInputCls } from '@/components/common/assignPickers';
import { lifecycleStates } from './schema';

/**
 * Assign every selected asset in one step (Marty, 01/09/2026). Offboarding a
 * leaver previously meant opening each device in turn — Assign, then Edit for
 * the note, then back to People — for every device they held.
 *
 * Notes are handled differently from the single-asset dialog on purpose: there
 * is no one "current note" to show across a mixed selection, so the choice is
 * between ADDING the same line to each asset's existing notes or REPLACING them
 * all. Replace is destructive across the whole selection, so it says so.
 */
export function BulkAssignDialog({
  open,
  onOpenChange,
  assetIds,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  assetIds: number[];
  onDone: (result: BulkAssignResult) => void;
}) {
  const [kind, setKind] = useState<BulkAssignBody['AssigneeKind']>('Person');
  const [assignee, setAssignee] = useState('');
  const [heldBy, setHeldBy] = useState('');
  const [status, setStatus] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');
  const [note, setNote] = useState('');
  const [noteMode, setNoteMode] = useState<'Append' | 'Replace'>('Append');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setKind('Person'); setAssignee(''); setHeldBy(''); setStatus('');
    setDeviceLocation(''); setNote(''); setNoteMode('Append'); setError(null);
  };

  const mutation = useMutation({
    mutationFn: (body: BulkAssignBody) => bulkAssignAssets(body),
    onSuccess: (result) => { onDone(result); onOpenChange(false); reset(); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not assign the selected assets.'),
  });

  const submit = () => {
    setError(null);
    if (!assignee) {
      setError(kind === 'Fleet' ? 'Enter a fleet item ID.' : `Select a ${kind.toLowerCase()}.`);
      return;
    }
    if (noteMode === 'Replace' && !note.trim()) {
      setError('Enter the replacement note, or switch back to adding to the existing notes.');
      return;
    }
    const body: BulkAssignBody = { AssetIds: assetIds, AssigneeKind: kind };
    if (kind === 'Person') body.PersonId = Number(assignee) || undefined;
    if (kind === 'Location') body.LocationId = Number(assignee) || undefined;
    if (kind === 'Fleet') body.FleetItemId = assignee || undefined;
    if (kind === 'Location' && heldBy) body.HeldByPersonId = Number(heldBy) || undefined;
    if (status) body.Status = status;
    if (deviceLocation) body.AssetLocationId = Number(deviceLocation) || undefined;
    if (note.trim()) { body.Note = note.trim(); body.NoteMode = noteMode; }
    mutation.mutate(body);
  };

  const count = assetIds.length;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}
      title={`Assign ${count} ${count === 1 ? 'asset' : 'assets'}`}
      confirmLabel={mutation.isPending ? 'Assigning…' : `Assign ${count}`}
      closeOnConfirm={false}
      onConfirm={submit}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        <p className="text-xs text-darkGrey/70">
          Every selected asset gets the same details. Each one keeps its own assignment history,
          and the change is recorded on each asset&apos;s Audit tab.
        </p>

        <label className="block text-xs font-semibold text-darkGrey">Assign to
          <select
            aria-label="Assignee kind"
            value={kind}
            onChange={(e) => { setKind(e.target.value as BulkAssignBody['AssigneeKind']); setAssignee(''); setHeldBy(''); setError(null); }}
            className={pickerInputCls}
          >
            <option value="Person">Person</option>
            <option value="Location">Location</option>
            <option value="Fleet">Fleet</option>
          </select>
        </label>

        {kind === 'Person' && <PersonPicker value={assignee} onChange={setAssignee} label="Person to assign to" />}
        {kind === 'Location' && <LocationSelect value={assignee} onChange={setAssignee} label="Location to assign to" />}
        {kind === 'Fleet' && (
          <input
            aria-label="Fleet item ID"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            placeholder="Fleet item ID"
            className={pickerInputCls}
          />
        )}

        {kind === 'Location' && (
          <label className="block text-xs font-semibold text-darkGrey">Held by (responsible person, optional)
            <PersonPicker value={heldBy} onChange={setHeldBy} label="Held by" />
          </label>
        )}

        <label className="block text-xs font-semibold text-darkGrey">Status (optional)
          <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)} className={pickerInputCls}>
            <option value="">Leave unchanged</option>
            {lifecycleStates.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        <label className="block text-xs font-semibold text-darkGrey">Device location (optional)
          <LocationSelect value={deviceLocation} onChange={setDeviceLocation} label="Device location" />
        </label>

        <fieldset className="rounded border border-border p-2">
          <legend className="px-1 text-xs font-semibold text-darkGrey">Notes</legend>
          <div className="mb-1.5 flex gap-4 text-xs font-normal text-darkGrey">
            <label className="flex items-center gap-1.5">
              <input type="radio" name="bulkNoteMode" checked={noteMode === 'Append'} onChange={() => setNoteMode('Append')} />
              Add to existing notes
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="bulkNoteMode" checked={noteMode === 'Replace'} onChange={() => setNoteMode('Replace')} />
              Replace notes
            </label>
          </div>
          {/* A JSX *attribute* string is not HTML, so an &apos; entity would render
              literally in the placeholder. Plain apostrophe, double-quoted string. */}
          <textarea
            aria-label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={noteMode === 'Replace' ? 'This text replaces the notes on every selected asset' : "Added to each asset's notes"}
            className={pickerInputCls}
          />
          {noteMode === 'Replace' && (
            <p className="mt-1 text-xs text-red">
              This overwrites the existing notes on all {count} selected {count === 1 ? 'asset' : 'assets'}.
            </p>
          )}
        </fieldset>
      </div>
    </AlertDialog>
  );
}
